import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';

import { cooperplaceApi } from 'services/api';
import { usePermission } from 'hooks';
import ApplicationLayout from 'v3/layouts/Application';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Input from 'v3/components/Input';
import Select from 'v3/components/Select';
import Pagination from 'v3/components/DataTable/Pagination';
import { DefaultEmptyComponent, DefaultLoadingComponent } from 'v3/components/List';
import { fetchCustomers } from 'utils/fetches';

import NegotiationCard from '../../Card';

function normalizeNegotiation(raw) {
  return {
    ...raw,
    active: Boolean(raw.active),
    autoRegistrationValidation: Boolean(raw.autoRegistrationValidation),
    autoGroValidation: Boolean(raw.autoGroValidation),
    autoArrivalByRadius: Boolean(raw.autoArrivalByRadius),
    integracoes: raw.automations || raw.integracoes || [],
  };
}

export default function NegotiationsList() {
  usePermission('VISUALIZAR_NEGOCIACOES', { redirect: true });

  const snackbar = useSnackbar();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    number: '',
    person: null,
  });
  const [fetchOptions, setFetchOptions] = useState({
    page: 1,
    perPage: 10,
    total: 0,
    lastPage: 0,
  });

  async function loadNegotiations(page = 1) {
    setLoading(true);
    try {
      const params = {
        page,
        perPage: fetchOptions.perPage,
      };
      if (filters.number) params.negotiationNumber = filters.number;
      if (filters.search) params.search = filters.search;
      if (filters.person?.id) params.personId = filters.person.id;

      const { data } = await cooperplaceApi.get('negotiation/list', { params });

      const items = data?.data || [];
      const meta = data?.meta || {};

      setNegotiations(items.map(normalizeNegotiation));
      setFetchOptions(prev => ({
        ...prev,
        page: meta.current_page || page,
        total: meta.total || 0,
        lastPage: meta.last_page || 0,
      }));
    } catch {
      snackbar.show(
        <Text>
          Não foi possível carregar as negociações. Use o botão Filtrar para
          tentar novamente.
        </Text>,
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNegotiations();
  }, []);

  function renderItem(item) {
    return <NegotiationCard key={item.id} negotiation={item} />;
  }

  return (
    <ApplicationLayout title="Negociações">
      <Row className="mb-3">
        <Col xs={12} md={3}>
          <Input
            label="Número Negociação"
            labelColor="#fff"
            value={filters.number}
            onChange={e =>
              setFilters(prev => ({ ...prev, number: e.target.value }))
            }
            placeholder="Filtrar por número"
          />
        </Col>
        <Col xs={12} md={3}>
          <Input
            label="Nome"
            labelColor="#fff"
            value={filters.search}
            onChange={e =>
              setFilters(prev => ({ ...prev, search: e.target.value }))
            }
            placeholder="Filtrar por nome"
          />
        </Col>
        <Col xs={12} md={3}>
          <Select.Async
            label="Tomador"
            labelColor="#fff"
            onSearch={search => fetchCustomers({ search })}
            value={filters.person}
            getOptionLabel={option =>
              `${option.social_name} - ${option.cgccpf}`
            }
            getOptionValue={option => option.id}
            onChange={selected =>
              setFilters(prev => ({ ...prev, person: selected }))
            }
            placeholder="Buscar tomador..."
          />
        </Col>
        <Col
          xs={12}
          md={3}
          style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}
        >
          <Button
            variant="primary"
            onClick={() => loadNegotiations(1)}
            loading={loading}
          >
            <Text color="white" type="regular" weight="500">
              Filtrar
            </Text>
          </Button>
        </Col>
      </Row>

      <Row className="row-eq-height">
        {loading ? (
          <DefaultLoadingComponent />
        ) : negotiations.length === 0 ? (
          <DefaultEmptyComponent />
        ) : (
          negotiations.map(renderItem)
        )}
      </Row>

      {fetchOptions.lastPage > 1 && (
        <Row className="mt-3 mb-4">
          <Col className="d-flex justify-content-end">
            <Pagination
              currentPage={fetchOptions.page}
              pages={fetchOptions.lastPage}
              onPageChanged={page => loadNegotiations(page)}
            />
          </Col>
        </Row>
      )}
    </ApplicationLayout>
  );
}
